import React from 'react';
import { cx } from './cx';

/** The tones match the chore statuses, so a status is never shown by colour alone. */
export type Tone = 'neutral' | 'brand' | 'overdue' | 'today' | 'upcoming' | 'success' | 'warning' | 'danger';

const TONES: Record<Tone, { badge: string; dot: string }> = {
  neutral: { badge: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300', dot: 'bg-gray-400 dark:bg-gray-500' },
  brand: { badge: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300', dot: 'bg-indigo-500 dark:bg-indigo-400' },
  overdue: { badge: 'bg-status-overdue-soft text-status-overdue-text dark:bg-red-500/15 dark:text-red-400', dot: 'bg-status-overdue dark:bg-red-400' },
  today: { badge: 'bg-status-today-soft text-status-today-text dark:bg-amber-500/15 dark:text-amber-400', dot: 'bg-status-today dark:bg-amber-400' },
  upcoming: { badge: 'bg-status-upcoming-soft text-status-upcoming-text dark:bg-indigo-500/15 dark:text-indigo-400', dot: 'bg-status-upcoming dark:bg-indigo-400' },
  success: { badge: 'bg-success-soft text-success-text dark:bg-emerald-500/15 dark:text-emerald-400', dot: 'bg-success dark:bg-emerald-400' },
  warning: { badge: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400', dot: 'bg-amber-500 dark:bg-amber-400' },
  danger: { badge: 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-400', dot: 'bg-red-500 dark:bg-red-400' },
};

export interface BadgeProps {
  tone?: Tone;
  /** A small dot before the text, for a status. */
  dot?: boolean;
  /** A tooltip explaining what the badge means. */
  title?: string;
  size?: 'sm' | 'md';
  className?: string;
  children: React.ReactNode;
}

/** A small, square-ish label for a status or a count: "5 days overdue", "Owner". */
export function Badge({ tone = 'neutral', dot, title, size = 'md', className, children }: BadgeProps) {
  return (
    <span title={title} className={cx('inline-flex items-center gap-1.5 whitespace-nowrap rounded-md text-xs font-semibold tabular-nums', size === 'sm' ? 'h-5 px-1.5' : 'h-6 px-2', TONES[tone].badge, className)}>
      {dot && <span className={cx('h-1.5 w-1.5 flex-none rounded-full', TONES[tone].dot)} aria-hidden="true" />}
      {children}
    </span>
  );
}

/** The dot alone, e.g. before a group heading. Decorative: say the status in text next to it. */
export function StatusDot({ tone, className }: { tone: Tone; className?: string }) {
  return <span className={cx('inline-block h-2 w-2 flex-none rounded-full', TONES[tone].dot, className)} aria-hidden="true" />;
}
